import * as React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { supabase } from "../logic/client";
import { FLOW } from "../logic/useWorkshopBlocker";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useI18n } from "../i18n/I18nProvider";


type Props = {
  handlePageChange: (page: string) => void;
};

const LABELS: Record<string, string> = {
  BlackoutKnowledgeQuestion: "Blackout Knowledge",
  AwarenessIntro: "Threat Perception",
  OthersSaid: "Prepare Yourself",
  StayOrLeave: "Scenario: Power Outage",
  ScenarioPowerOutage: "Scenario: Burning Building",
  ScenarioBurningBuilding: "Scenario: Securing Documents",
  ScenarioSecuringDocuments: "Scenario: Emergency Simulation",
  ScenarioEmergency: "Scenario: Recovery Position",
  ScenarioRecoveryPosition: "Scenario: Communication Failure",
  ScenarioCommunicationFailure: "Scenario: Sirene",
  ScenarioSirene: "Scenario: Crisis",
  ScenarioCrisis: "Scenario: Orientation",
  ScenarioOrientation: "Scenario: Information Overload",
  ScenarioInformationOverload: "Scenario: Mental Overwhelm",
  ScenarioMentalOverwhelm: "Scenario: Isolation",
  ScenarioIsolation: "Scenario: Evacuation",
  ScenarioEvacuation: "Evacuation Task Reflection",
  EvacuationTaskReview: "Scenario: Physical Limit",
  ScenarioPhysicalLimit: "Family Preparedness",
  FamilyPreparedness: "Community Preparedness",
  CommunityPreparedness: "Availability Awareness",
  EmployeeScreen: "Take a Photo",
  TakeAPhoto: "Share Invitation",
  ShareInvitation: "HOME SCREEN",
};

export default function ScreenManagement(props: Props) {
  const { handlePageChange } = props;
  const { lang } = useI18n();
  const L = (en: string, de: string) => (lang === "de" ? de : en);

  const [currentScreen, setCurrentScreen] = React.useState<string>("");
  const [workshopId, setWorkshopId] = React.useState<string | null>(null);
  const [isWorkshopLoading, setIsWorkshopLoading] = React.useState(true);

  const [isMuted, setIsMuted] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const visibleFlow = React.useMemo(
    () => FLOW.filter((s) => LABELS[s]),
    []
  );

  /* ---------------- INIT ---------------- */

  /* ---------------- SIREN INIT ---------------- */

  React.useEffect(() => {
    audioRef.current = new Audio("/siren.mp3");
    audioRef.current.loop = true;

    return () => {
      audioRef.current?.pause();
    };
  }, []);

  /* ---------------- SIREN CONTROL ---------------- */

  React.useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    if (currentScreen === "ScenarioSirene") {
      audio.currentTime = 0;
      if (!isMuted) {
        audio.play().catch(() => {});
      }
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [currentScreen, isMuted]);

  function toggleMute(e: React.MouseEvent) {
    e.stopPropagation();

    const audio = audioRef.current;
    if (!audio) return;

    setIsMuted((prev) => {
      const newState = !prev;
      if (newState) {
        audio.pause();
      } else {
        if (currentScreen === "ScenarioSirene") {
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }
      }
      return newState;
    });
  }

  /* ---------------- DATA ---------------- */

  const loadActiveWorkshop = React.useCallback(async () => {
    setIsWorkshopLoading(true);

    const { data, error } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Loading active workshop failed:", error);
      setWorkshopId(null);
      setIsWorkshopLoading(false);
      return;
    }

    setWorkshopId(data?.id ?? null);
    setIsWorkshopLoading(false);
  }, []);

  React.useEffect(() => {
    loadActiveWorkshop();
  }, [loadActiveWorkshop]);

  React.useEffect(() => {
    if (!workshopId) return;
    let channel: any;

    async function init() {
      // initial fetch
      const { data } = await supabase
        .from("workshop_live_state")
        .select("current_screen")
        .eq("workshop_id", workshopId)
        .single();

      if (data) setCurrentScreen(data.current_screen);

      // realtime subscription
      channel = supabase
        .channel("workshop-screen")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "workshop_live_state",
            filter: `workshop_id=eq.${workshopId}`,
          },
          (payload) => {
            setCurrentScreen(
              (payload.new as { current_screen: string }).current_screen
            );
          }
        )
        .subscribe();
    }

    init();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [workshopId]);

  async function setScreen(screen: string) {
    setCurrentScreen(screen);

    await supabase
      .from("workshop_live_state")
      .update({ current_screen: screen })
      .eq("workshop_id", workshopId);
  }

  const blockedIndex =
    currentScreen === "N/A"
      ? visibleFlow.length
      : visibleFlow.indexOf(currentScreen);

  /* ---------------- UI ---------------- */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        position: "relative",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
      }}
    >
      {/* 🔙 BACK */}
      <Box
        sx={{
          position: "absolute",
          top: isTablet ? 12 : 24,
          left: isTablet ? 12 : 24,
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={() => handlePageChange("ControlPanel")}
          sx={{
            color: "white",
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.1)",
            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Typography
        fontWeight={800}
        mb={4}
        sx={{ color: "#fff", fontSize: "1.6rem" }}
      >
        {L("Manage Workshop Flow", "Workshop-Ablauf steuern")}
      </Typography>

      {isWorkshopLoading ? (
        <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
          {L("Loading workshop...", "Workshop wird geladen...")}
        </Typography>
      ) : !workshopId ? (
        <Paper
          sx={{
            width: "100%",
            maxWidth: 560,
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            textAlign: "center",
            color: "#fff",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
            border: "1px solid rgba(255,255,255,0.10)",
            backdropFilter: "blur(14px)",
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 900, mb: 1.5 }}>
            {L("No active workshop", "Kein aktiver Workshop")}
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.74)", mb: 3 }}>
            {L(
              "You have not activated any workshop yet. Please go to the Workshop Management page and activate a workshop to access the screens.",
              "Du hast noch keinen Workshop aktiviert. Bitte gehe zur Workshop-Verwaltung und aktiviere einen Workshop, um auf die Screens zuzugreifen."
            )}
          </Typography>

          <Button
            onClick={() => handlePageChange("WorkshopManagement")}
            sx={{
              px: 3,
              py: 1.4,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 800,
              color: "#fff",
              background: "#e40505",
              "&:hover": { background: "#a10707" },
            }}
          >
            {L("Go to Workshop Management", "Zur Workshop-Verwaltung")}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3} maxWidth={900}>
          {visibleFlow.map((screen, index) => {
            const isCompleted = index < blockedIndex - 1;
            const isOngoing = blockedIndex > 0 && index === blockedIndex - 1;
            const isBlocked = index === blockedIndex;
            const isLocked = index > blockedIndex;

            return (
              <Grid item xs={6} sm={4} key={screen}>
                <Paper
                  onClick={() => {
                    if (isCompleted || isOngoing) {
                      setScreen(screen);
                    }

                    if (isBlocked) {
                      const next = visibleFlow[index + 1];
                      setScreen(next ?? "N/A");
                    }
                  }}
                  sx={{
                    position: "relative",
                    height: 140,
                    borderRadius: 5,
                    cursor:
                      isBlocked || isCompleted || isOngoing
                        ? "pointer"
                        : "not-allowed",

                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,

                    // FIXED COLORS
                    background: isLocked
                      ? "rgba(255,255,255,0.05)"
                      : isBlocked
                      ? "linear-gradient(135deg, #444, #222)"
                      : isOngoing
                      ? "linear-gradient(135deg, #ffc107, #7a5a00)"
                      : "linear-gradient(135deg, #0f5132, #06281a)",

                    border: isBlocked
                      ? "2px dashed rgba(255,255,255,0.5)"
                      : isOngoing
                      ? "2px solid #ffc107"
                      : "1px solid rgba(255,255,255,0.08)",

                    color: isLocked
                      ? "rgba(255,255,255,0.3)"
                      : "#fff",

                    opacity: isLocked ? 0.4 : 1,

                    transition: "all .25s ease",

                    "&:hover":
                      isBlocked || isCompleted || isOngoing
                        ? {
                            transform: "scale(1.05)",
                            boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
                          }
                        : {},
                  }}
                >
                  {/* 🔊 MUTE BUTTON */}
                  {screen === "ScenarioCommunicationFailure" && !isLocked && (
                    <IconButton
                      onClick={toggleMute}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#fff",
                        background: "rgba(0,0,0,0.4)",
                        "&:hover": {
                          background: "rgba(0,0,0,0.6)",
                        },
                      }}
                    >
                      {isMuted ? "🔇" : "🔊"}
                    </IconButton>
                  )}

                  <Typography fontWeight={700}>
                    {LABELS[screen]}
                  </Typography>

                  {isBlocked && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#ccc" }}>
                      TAP TO UNBLOCK
                    </Typography>
                  )}

                  {isOngoing && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#ffc107" }}>
                      ONGOING
                    </Typography>
                  )}

                  {isCompleted && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#00ff88" }}>
                      COMPLETED
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
